import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type MouseEvent, useState } from "react";
import { MdAdd, MdExpandLess, MdExpandMore, MdSettings } from "react-icons/md";

import AppIcon from "@/components/Core/AppIcon";
import { getMediaUrl } from "@/configs";
import { useBalance, useDialog, useTranslate, useUser } from "@/hooks";
import { WALLET_UNIT, toShown } from "@/lib/wallet/show";
import {
  changeCurrency,
  getBalance,
  getCryptoCurrencies,
  getPlayerWallets,
  getWalletCurrencies,
} from "@/services";
import { dispatch, updateBalance, updateUser } from "@/store";
import { colors } from "@/theme";
import { handleError } from "@/utils";
import AssetMark from "@/views/Profile/Wallet/AssetMark";
import {
  CRYPTO_OPTIONS_QUERY_KEY,
  PLAYER_WALLETS_QUERY_KEY,
  WALLET_OPTIONS_QUERY_KEY,
  readHideEmptyWallets,
} from "@/views/Profile/Wallet/data";

import { CURRENCY_ICON } from "./data";

const Wallet = () => {
  const user = useUser();
  const balance = useBalance();
  const { openDialog } = useDialog();
  const t = useTranslate();
  const queryClient = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [switching, setSwitching] = useState("");

  const currency = user?.currency || "";
  // The server's answer names the row the amount came from, so the mark can never disagree with it.
  const base = balance?.baseCurrency || user?.baseCurrency || currency;
  const iconName = CURRENCY_ICON[base.toUpperCase()];

  const { data: walletOptions = [] } = useQuery({
    queryKey: WALLET_OPTIONS_QUERY_KEY,
    queryFn: getWalletCurrencies,
  });
  const { data: playerWallets } = useQuery({
    queryKey: PLAYER_WALLETS_QUERY_KEY,
    queryFn: getPlayerWallets,
    enabled: !!anchor,
    staleTime: 0,
  });
  const { data: menuOptions = [] } = useQuery({
    queryKey: CRYPTO_OPTIONS_QUERY_KEY,
    queryFn: getCryptoCurrencies,
  });
  const baseIcon = [...menuOptions, ...walletOptions].find((option) => option.value === base)?.icon;
  // SKY is always offered; the wallet dialog, which deposits and withdraws, leaves it out.
  const coinOptions = walletOptions.filter((option) => option.kind === "coin");
  // The active wallet and SKY stay listed, empty or not, so the list always shows where the player is.
  const hideEmpty = readHideEmptyWallets();
  const walletOf = (code: string) =>
    playerWallets?.wallets.find((item) => item.baseCurrency === code);
  const balanceOf = (code: string) => walletOf(code)?.balance ?? 0;
  // Biggest first, compared in USD so SKY sorts in with the money wallets.
  const usdValueOf = (code: string) => walletOf(code)?.usdValue ?? balanceOf(code);
  const listed = [...coinOptions, ...menuOptions]
    .sort((a, b) => usdValueOf(b.value) - usdValueOf(a.value))
    .filter(
      (option) =>
        !hideEmpty ||
        option.value === base ||
        option.kind === "coin" ||
        balanceOf(option.value) > 0,
    );
  // The symbol when the list knows one, else the code; a non-breaking space then joins it to the number.
  const symbolOf = (code: string) =>
    walletOptions.find((option) => option.value === code)?.symbol || code;
  // Wallets hold USD; the header shows it in the player's show currency.
  const unit = balance?.currency || WALLET_UNIT;
  const shownAmount = toShown(balance?.balance ?? 0, unit);
  const symbol = symbolOf(shownAmount.currency);
  // SKY is a coin, not money, so the pill shows the bare number when it is active.
  const baseIsCoin = coinOptions.some((option) => option.value === base);
  // Always two decimals, so an empty wallet reads 0.00 rather than 0.
  const formatAmount = (value: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const switchTo = async (code: string) => {
    if (switching) return;
    if (code === base) {
      setAnchor(null);
      return;
    }
    setSwitching(code);
    try {
      dispatch(updateUser(await changeCurrency({ currency: code })));
      dispatch(updateBalance(await getBalance()));
      queryClient.invalidateQueries({ queryKey: PLAYER_WALLETS_QUERY_KEY });
      setAnchor(null);
    } catch (error) {
      handleError(error);
    } finally {
      setSwitching("");
    }
  };

  const openCashier = (event: MouseEvent) => {
    event.stopPropagation();
    openDialog("wallet");
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        pl={1.25}
        pr={0.5}
        py={0.5}
        role="button"
        tabIndex={0}
        aria-label={t("header.wallet")}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        onClick={(e) => setAnchor(e.currentTarget)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchor(e.currentTarget);
          }
        }}
        sx={(theme) => ({
          cursor: "pointer",
          borderRadius: 999,
          minHeight: 38,
          backgroundColor: colors.white,
          border: `1px solid ${colors.border}`,
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          "&:hover": {
            borderColor: colors.prime,
            boxShadow: `0 4px 14px ${alpha(colors.prime, 0.22)}`,
          },
          "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: 2 },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          ...theme.applyStyles("dark", {
            background: colors.layer1Dark,
            border: `1px solid ${colors.borderDark}`,
          }),
        })}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            "& svg": { width: 24, height: 24 },
          }}
        >
          {!baseIcon && iconName ? (
            <AppIcon name={iconName} size={24} />
          ) : (
            <AssetMark code={base} image={getMediaUrl(baseIcon)} />
          )}
        </Box>

        <Typography
          sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {!baseIsCoin && `${symbol}\u00a0`}
          {formatAmount(shownAmount.amount)}
        </Typography>

        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            width: 24,
            height: 24,
            display: "grid",
            placeItems: "center",
            borderRadius: "6px",
            fontSize: 18,
            color: "text.primary",
            backgroundColor: alpha(colors.prime, 0.18),
          }}
        >
          {anchor ? <MdExpandLess /> : <MdExpandMore />}
        </Box>

        <ButtonBase
          onClick={openCashier}
          aria-label={t("wallet.cashier")}
          // Enter on this button must not also reach the pill's handler and open the list.
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: "999px",
            fontSize: 20,
            color: colors.white,
            backgroundColor: "#2E90FF",
            boxShadow: "0 4px 12px rgba(46, 144, 255, 0.4)",
            "&:hover": { filter: "brightness(1.08)" },
            "&:focus-visible": { outline: "2px solid #5BC8FF", outlineOffset: 2 },
          }}
        >
          <MdAdd aria-hidden />
        </ButtonBase>
      </Stack>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            sx: { width: 250, maxHeight: "80vh", mt: 1, borderRadius: "16px", overflow: "auto" },
          },
          list: { sx: { p: 1.25 } },
        }}
      >
        {listed.map((option, index) => {
          const row = playerWallets?.wallets.find((item) => item.baseCurrency === option.value);
          return (
            <MenuItem
              key={option.value}
              selected={option.value === base}
              disabled={!!switching}
              onClick={() => switchTo(option.value)}
              sx={{
                gap: 1.25,
                py: 1,
                backgroundColor: alpha(colors.prime, 0.1),
                "&.Mui-selected, &.Mui-selected:hover": {
                  backgroundColor: alpha(colors.prime, 0.3),
                },
                borderTopLeftRadius: index === 0 ? "12px" : 0,
                borderTopRightRadius: index === 0 ? "12px" : 0,
                borderBottomLeftRadius: index === listed.length - 1 ? "12px" : 0,
                borderBottomRightRadius: index === listed.length - 1 ? "12px" : 0,
              }}
            >
              <AssetMark code={option.value} image={getMediaUrl(option.icon)} />
              <Typography sx={{ flex: 1, fontWeight: 700 }}>{option.label}</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "success.main",
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {/* SKY is a coin, not money, so its row shows the bare number. */}
                {option.kind !== "coin" &&
                  `${symbolOf(toShown(row?.balance ?? 0, row?.currency || unit).currency)}\u00a0`}
                {formatAmount(toShown(row?.balance ?? 0, row?.currency || unit).amount)}
              </Typography>
            </MenuItem>
          );
        })}
        {listed.length > 0 && <Divider sx={{ my: 1.25 }} />}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            openDialog("wallet-settings");
          }}
          sx={{ gap: 1, py: 1, justifyContent: "center", fontWeight: 700, borderRadius: "12px" }}
        >
          <MdSettings aria-hidden />
          {t("wallet.settings")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default Wallet;
